import React, { Component } from "react";
import PropTypes from "prop-types";
import { area as d3Area } from "d3-shape";

import GenericChartComponent from "../GenericChartComponent";
import { getAxisCanvas } from "../GenericComponent";

import {
  hexToRGBA,
  isDefined,
  first,
  functor,
  strokeDashTypes,
  getStrokeDasharray,
} from "../utils";

class AreaOnlySeries extends Component {
  constructor(props) {
    super(props);
    this.renderSVG = this.renderSVG.bind(this);
    this.drawOnCanvas = this.drawOnCanvas.bind(this);
  }
  drawOnCanvas(ctx, moreProps) {
    const { yAccessor, defined, base, canvasGradient } = this.props;
    const { fill, opacity, interpolation, canvasClip, stroke } = this.props;

    const strokeColor = stroke?.color || "none";
    const strokeOpacity = stroke?.opacity ?? 1;
    const strokeWidth = stroke?.width ?? 1;
    const strokeDasharray = stroke?.dash ?? "Solid";

    const {
      xScale,
      chartConfig: { yScale },
      plotData,
      xAccessor,
    } = moreProps;

    if (canvasClip) {
      ctx.save();
      canvasClip(ctx, moreProps);
    }

    if (canvasGradient != null) {
      ctx.fillStyle = canvasGradient(moreProps, ctx);
    } else {
      ctx.fillStyle = hexToRGBA(fill, opacity);
    }
    ctx.strokeStyle = hexToRGBA(strokeColor, strokeOpacity);
    ctx.lineWidth = strokeWidth;
    ctx.setLineDash(getStrokeDasharray(strokeDasharray).split(","));

    ctx.beginPath();
    const newBase = functor(base);
    const areaSeries = d3Area()
      .defined((d) => defined(yAccessor(d)))
      .x((d) => xScale(xAccessor(d)))
      .y0((d) => newBase(yScale, d, moreProps))
      .y1((d) => yScale(yAccessor(d)))
      .context(ctx);

    if (isDefined(interpolation)) {
      areaSeries.curve(interpolation);
    }
    areaSeries(plotData);
    ctx.fill();
    // Optionally stroke the area boundary if stroke is set
    if (strokeColor !== "none" && strokeWidth > 0) {
      ctx.stroke();
    }

    if (canvasClip) {
      ctx.restore();
    }
  }
  renderSVG(moreProps) {
    const {
      yAccessor,
      defined,
      base,
      style,
      stroke,
      fill,
      className,
      opacity,
      interpolation,
    } = this.props;

    const strokeColor = stroke?.color || "none";
    const strokeOpacity = stroke?.opacity ?? 1;
    const strokeWidth = stroke?.width ?? 1;
    const strokeDasharray = stroke?.dash ?? "Solid";

    const {
      xScale,
      chartConfig: { yScale },
      plotData,
      xAccessor,
    } = moreProps;

    const newBase = functor(base);
    const areaSeries = d3Area()
      .defined((d) => defined(yAccessor(d)))
      .x((d) => xScale(xAccessor(d)))
      .y0((d) => newBase(yScale, d, moreProps))
      .y1((d) => yScale(yAccessor(d)));

    if (isDefined(interpolation)) {
      areaSeries.curve(interpolation);
    }

    const d = areaSeries(plotData);
    const newClassName = className.concat(
      isDefined(strokeColor) ? "" : " line-stroke",
    );
    return (
      <path
        style={style}
        d={d}
        stroke={strokeColor}
        strokeOpacity={strokeOpacity}
        strokeWidth={strokeWidth}
        strokeDasharray={getStrokeDasharray(strokeDasharray)}
        fill={hexToRGBA(fill, opacity)}
        className={newClassName}
      />
    );
  }
  render() {
    return (
      <GenericChartComponent
        svgDraw={this.renderSVG}
        canvasDraw={this.drawOnCanvas}
        canvasToDraw={getAxisCanvas}
        drawOn={["pan", "drag", "mousemove"]}
      />
    );
  }
}

AreaOnlySeries.propTypes = {
  className: PropTypes.string,
  yAccessor: PropTypes.func.isRequired,
  stroke: PropTypes.shape({
    color: PropTypes.string,
    opacity: PropTypes.number,
    width: PropTypes.number,
    dash: PropTypes.oneOf(strokeDashTypes),
  }),
  fill: PropTypes.string,
  opacity: PropTypes.number,
  defined: PropTypes.func,
  base: PropTypes.oneOfType([PropTypes.func, PropTypes.number]),
  interpolation: PropTypes.func,
  canvasClip: PropTypes.func,
  style: PropTypes.object,
  canvasGradient: PropTypes.func,
};

AreaOnlySeries.defaultProps = {
  className: "line ",
  fill: "none",
  opacity: 1,
  defined: (d) => !isNaN(d),
  base: (yScale /* , d, moreProps */) => first(yScale.range()),
  stroke: {
    color: "none",
    opacity: 1,
    width: 1,
    dash: "Solid",
  },
};

export default AreaOnlySeries;
